import React, { useState, useEffect } from 'react';
import { X, User, Mail, Lock, Briefcase, Building2, DollarSign, Clock, Users, Award, Plus, Trash2, Linkedin, Globe, Tag } from 'lucide-react';
import { apiService } from '../../services/api';
import ProfileImageUpload from '../common/ProfileImageUpload';
import '../../styles/create-mentor-modal.scss';

interface CreateMentorModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface ExpertiseArea {
  id: number;
  name: string;
  category: string;
  icon?: string;
}

interface MentorFormData {
  // User info
  email: string;
  password: string;
  confirmPassword: string;
  first_name: string;
  last_name: string;
  phone?: string;
  bio?: string;
  profile_image?: File | null;
  linkedin_url?: string;
  website_url?: string;

  // Mentor profile
  title: string;
  company: string;
  years_experience: number;
  hourly_rate: number;
  availability_hours: number;
  max_mentees: number;
  about_mentoring?: string;
  mentoring_approach?: string;
  languages_spoken: string[];
  skills: string[];

  // Expertise areas
  expertise: Array<{
    expertise_area_id: number;
    proficiency_level: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  }>;
}

const CreateMentorModal: React.FC<CreateMentorModalProps> = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [expertiseAreas, setExpertiseAreas] = useState<ExpertiseArea[]>([]);
  const [currentLanguage, setCurrentLanguage] = useState('');
  const [currentSkill, setCurrentSkill] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<MentorFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    bio: '',
    profile_image: null,
    linkedin_url: '',
    website_url: '',
    title: '',
    company: '',
    years_experience: 0,
    hourly_rate: 0,
    availability_hours: 5,
    max_mentees: 5,
    about_mentoring: '',
    mentoring_approach: '',
    languages_spoken: [],
    skills: [],
    expertise: []
  });

  useEffect(() => {
    loadExpertiseAreas();
  }, []);

  const loadExpertiseAreas = async () => {
    try {
      const response = await apiService.common.getExpertiseAreas();
      if (response.data.success) {
        setExpertiseAreas(response.data.data);
      }
    } catch (error) {
      console.error('Error loading expertise areas:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
  };

  const handleImageSelected = (file: File) => {
    setFormData(prev => ({ ...prev, profile_image: file }));
  };

  const handleImageRemoved = () => {
    setFormData(prev => ({ ...prev, profile_image: null }));
  };

  const addLanguage = () => {
    if (currentLanguage.trim() && !formData.languages_spoken.includes(currentLanguage.trim())) {
      setFormData(prev => ({
        ...prev,
        languages_spoken: [...prev.languages_spoken, currentLanguage.trim()]
      }));
      setCurrentLanguage('');
    }
  };

  const removeLanguage = (language: string) => {
    setFormData(prev => ({
      ...prev,
      languages_spoken: prev.languages_spoken.filter(l => l !== language)
    }));
  };

  const addSkill = () => {
    if (currentSkill.trim() && !formData.skills.includes(currentSkill.trim())) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, currentSkill.trim()]
      }));
      setCurrentSkill('');
    }
  };

  const removeSkill = (skill: string) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skill)
    }));
  };

  const addExpertise = () => {
    if (formData.expertise.length < 10) {
      setFormData(prev => ({
        ...prev,
        expertise: [...prev.expertise, { expertise_area_id: 0, proficiency_level: 'intermediate' }]
      }));
    }
  };

  const updateExpertise = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      expertise: prev.expertise.map((exp, i) =>
        i === index ? { ...exp, [field]: value } : exp
      )
    }));
  };

  const removeExpertise = (index: number) => {
    setFormData(prev => ({
      ...prev,
      expertise: prev.expertise.filter((_, i) => i !== index)
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // User validation
    if (!formData.email) newErrors.email = 'Email es requerido';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email inválido';

    if (!formData.password) newErrors.password = 'Contraseña es requerida';
    else if (formData.password.length < 6) newErrors.password = 'Mínimo 6 caracteres';

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    if (!formData.first_name) newErrors.first_name = 'Nombre es requerido';
    if (!formData.last_name) newErrors.last_name = 'Apellido es requerido';

    // Mentor profile validation
    if (!formData.title) newErrors.title = 'Título es requerido';
    if (!formData.company) newErrors.company = 'Empresa es requerida';
    if (formData.years_experience < 0) newErrors.years_experience = 'Experiencia debe ser positiva';
    if (formData.hourly_rate < 0) newErrors.hourly_rate = 'Tarifa debe ser positiva';
    if (formData.max_mentees < 1) newErrors.max_mentees = 'Debe aceptar al menos 1 mentee';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);

      // Create FormData for file upload
      const submitData = new FormData();

      // User data
      submitData.append('email', formData.email);
      submitData.append('password', formData.password);
      submitData.append('first_name', formData.first_name);
      submitData.append('last_name', formData.last_name);
      submitData.append('role_name', 'mentor');

      if (formData.phone) submitData.append('phone', formData.phone);
      if (formData.bio) submitData.append('bio', formData.bio);
      if (formData.linkedin_url) submitData.append('linkedin_url', formData.linkedin_url);
      if (formData.website_url) submitData.append('website_url', formData.website_url);
      if (formData.profile_image) submitData.append('profile_image', formData.profile_image);

      // Mentor profile data
      submitData.append('title', formData.title);
      submitData.append('company', formData.company);
      submitData.append('years_experience', formData.years_experience.toString());
      submitData.append('hourly_rate', formData.hourly_rate.toString());
      submitData.append('availability_hours', formData.availability_hours.toString());
      submitData.append('max_mentees', formData.max_mentees.toString());

      if (formData.about_mentoring) submitData.append('about_mentoring', formData.about_mentoring);
      if (formData.mentoring_approach) submitData.append('mentoring_approach', formData.mentoring_approach);

      // Languages as JSON array
      submitData.append('languages_spoken', JSON.stringify(formData.languages_spoken));

      // Skills as JSON array
      submitData.append('skills', JSON.stringify(formData.skills));

      // Expertise as JSON array
      const validExpertise = formData.expertise.filter(exp => exp.expertise_area_id > 0);
      submitData.append('expertise', JSON.stringify(validExpertise));

      // Don't set Content-Type header - let axios set it with the correct boundary
      await apiService.post('/admin/mentors', submitData);

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error creating mentor:', error);
      setErrors({
        submit: error.response?.data?.message || 'Error al crear mentor. Intenta nuevamente.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-mentor-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header__content">
            <User size={24} className="header-icon" />
            <div>
              <h2>Crear Nuevo Mentor</h2>
              <p className="modal-subtitle">Complete la información del mentor</p>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {errors.submit && (
              <div className="alert alert--danger">
                {errors.submit}
              </div>
            )}

            {/* Profile Image */}
            <div className="form-section">
              <h3 className="form-section__title">Foto de Perfil</h3>
              <ProfileImageUpload
                onImageSelected={handleImageSelected}
                onImageRemoved={handleImageRemoved}
                size="lg"
                maxSize={5}
              />
            </div>

            {/* Personal Information */}
            <div className="form-section">
              <h3 className="form-section__title">Información Personal</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="first_name">Nombre *</label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className={errors.first_name ? 'error' : ''}
                  />
                  {errors.first_name && <span className="error-text">{errors.first_name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="last_name">Apellido *</label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className={errors.last_name ? 'error' : ''}
                  />
                  {errors.last_name && <span className="error-text">{errors.last_name}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={errors.email ? 'error' : ''}
                  />
                  {errors.email && <span className="error-text">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Teléfono</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="password">Contraseña *</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={errors.password ? 'error' : ''}
                    placeholder="Mínimo 8 caracteres"
                  />
                  {errors.password && <span className="error-text">{errors.password}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirmar Contraseña *</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={errors.confirmPassword ? 'error' : ''}
                  />
                  {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="bio">Biografía</label>
                <textarea
                  id="bio"
                  name="bio"
                  rows={3}
                  value={formData.bio}
                  onChange={handleInputChange}
                  placeholder="Breve descripción profesional..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="linkedin_url">LinkedIn</label>
                  <input
                    type="url"
                    id="linkedin_url"
                    name="linkedin_url"
                    value={formData.linkedin_url}
                    onChange={handleInputChange}
                    placeholder="https://linkedin.com/in/usuario"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="website_url">Sitio Web</label>
                  <input
                    type="url"
                    id="website_url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleInputChange}
                    placeholder="https://tusitio.com"
                  />
                </div>
              </div>
            </div>

            {/* Professional Information */}
            <div className="form-section">
              <h3 className="form-section__title">Información Profesional</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="title">Título/Cargo *</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className={errors.title ? 'error' : ''}
                    placeholder="Ej: Senior Software Engineer"
                  />
                  {errors.title && <span className="error-text">{errors.title}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="company">Empresa *</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className={errors.company ? 'error' : ''}
                  />
                  {errors.company && <span className="error-text">{errors.company}</span>}
                </div>
              </div>

              <div className="form-row form-row--3">
                <div className="form-group">
                  <label htmlFor="years_experience">Años de Experiencia *</label>
                  <input
                    type="number"
                    id="years_experience"
                    name="years_experience"
                    value={formData.years_experience}
                    onChange={handleNumberChange}
                    min="0"
                    className={errors.years_experience ? 'error' : ''}
                  />
                  {errors.years_experience && <span className="error-text">{errors.years_experience}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="hourly_rate">Tarifa/Hora ($)</label>
                  <input
                    type="number"
                    id="hourly_rate"
                    name="hourly_rate"
                    value={formData.hourly_rate}
                    onChange={handleNumberChange}
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="availability_hours">Horas Disponibles/Semana</label>
                  <input
                    type="number"
                    id="availability_hours"
                    name="availability_hours"
                    value={formData.availability_hours}
                    onChange={handleNumberChange}
                    min="1"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="max_mentees">Máximo de Mentees *</label>
                <input
                  type="number"
                  id="max_mentees"
                  name="max_mentees"
                  value={formData.max_mentees}
                  onChange={handleNumberChange}
                  min="1"
                  max="20"
                  className={errors.max_mentees ? 'error' : ''}
                />
                {errors.max_mentees && <span className="error-text">{errors.max_mentees}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="about_mentoring">Sobre Mentoría</label>
                <textarea
                  id="about_mentoring"
                  name="about_mentoring"
                  rows={3}
                  value={formData.about_mentoring}
                  onChange={handleInputChange}
                  placeholder="¿Qué te motiva como mentor? ¿Qué esperas lograr?"
                />
              </div>

              <div className="form-group">
                <label htmlFor="mentoring_approach">Enfoque de Mentoría</label>
                <textarea
                  id="mentoring_approach"
                  name="mentoring_approach"
                  rows={3}
                  value={formData.mentoring_approach}
                  onChange={handleInputChange}
                  placeholder="Describe tu estilo y metodología de mentoría..."
                />
              </div>
            </div>

            {/* Languages */}
            <div className="form-section">
              <h3 className="form-section__title">Idiomas</h3>
              <div className="languages-input">
                <input
                  type="text"
                  value={currentLanguage}
                  onChange={(e) => setCurrentLanguage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLanguage())}
                  placeholder="Ej: Español, Inglés, Francés..."
                />
                <button type="button" onClick={addLanguage} className="btn btn--outline btn--sm">
                  <Plus size={16} /> Agregar
                </button>
              </div>
              {formData.languages_spoken.length > 0 && (
                <div className="languages-list">
                  {formData.languages_spoken.map((lang, idx) => (
                    <div key={idx} className="language-tag">
                      {lang}
                      <button type="button" onClick={() => removeLanguage(lang)}>
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Skills */}
            <div className="form-section">
              <h3 className="form-section__title">
                <Tag size={18} /> Habilidades / Skills
              </h3>
              <div className="languages-input">
                <input
                  type="text"
                  value={currentSkill}
                  onChange={(e) => setCurrentSkill(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                  placeholder="Ej: React, Python, Liderazgo, Scrum..."
                />
                <button type="button" onClick={addSkill} className="btn btn--outline btn--sm">
                  <Plus size={16} /> Agregar
                </button>
              </div>
              {formData.skills.length > 0 && (
                <div className="languages-list">
                  {formData.skills.map((skill, idx) => (
                    <div key={idx} className="language-tag skill-tag">
                      {skill}
                      <button type="button" onClick={() => removeSkill(skill)}>
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Expertise Areas */}
            <div className="form-section">
              <h3 className="form-section__title">Áreas de Expertise</h3>
              <div className="expertise-list">
                {formData.expertise.map((exp, index) => (
                  <div key={index} className="expertise-item">
                    <div className="form-row">
                      <div className="form-group" style={{ flex: 2 }}>
                        <select
                          value={exp.expertise_area_id}
                          onChange={(e) => updateExpertise(index, 'expertise_area_id', parseInt(e.target.value))}
                        >
                          <option value="0">Seleccionar área...</option>
                          {expertiseAreas.map(area => (
                            <option key={area.id} value={area.id}>
                              {area.name} ({area.category})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group" style={{ flex: 1 }}>
                        <select
                          value={exp.proficiency_level}
                          onChange={(e) => updateExpertise(index, 'proficiency_level', e.target.value)}
                        >
                          <option value="beginner">Principiante</option>
                          <option value="intermediate">Intermedio</option>
                          <option value="advanced">Avanzado</option>
                          <option value="expert">Experto</option>
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeExpertise(index)}
                        className="btn-icon btn-icon--danger"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {formData.expertise.length < 10 && (
                <button type="button" onClick={addExpertise} className="btn btn--outline btn--sm">
                  <Plus size={16} /> Agregar Expertise
                </button>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn--outline" onClick={onClose} disabled={loading}>
              Cancelar
            </button>
            <button type="submit" className="btn btn--primary" disabled={loading}>
              {loading ? 'Creando...' : 'Crear Mentor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateMentorModal;
